"use client";

import React from "react";
import {
  InputGroup,
  CloseButton,
  Textarea,
  Input,
  Tokens,
} from "@chakra-ui/react";
import usePageSearch from "../hooks/usePageSearch";
import OpenSearchFiltersButton from "./OpenSearchFiltersButton";

interface NavbarSearchInputProps {
  onCloseSuccess?: () => void;
  onFocusAction?: React.FocusEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  >;
  onBlurAction?: React.FocusEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  >;
  placeholder?: string;
  debounceMs?: number;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  textareaRef?: React.RefObject<HTMLTextAreaElement | null>;
  hideInternalClearButton?: boolean;
  emptyEndElement?: React.ReactNode;
  showIdleSearchFiltersButton?: boolean;
  border?: Tokens["borders"];
  borderColor?: Tokens["colors"] | string;
  borderColorOnFocus?: Tokens["colors"] | string;
  borderRadius?: Tokens["radii"];
  singleLine?: boolean;
  disabled?: boolean;
}

function NavbarSearchInput({
  onCloseSuccess,
  onFocusAction,
  onBlurAction,
  placeholder = "Search...",
  inputRef,
  textareaRef,
  hideInternalClearButton = true,
  emptyEndElement,
  showIdleSearchFiltersButton = true,
  border = "md",
  borderColor = "moveon.primaryLight",
  borderColorOnFocus = "moveon.primary",
  borderRadius = "md",
  singleLine = false,
  disabled = false,
}: NavbarSearchInputProps) {
  const { isAllowedPage, pageSearch } = usePageSearch();
  const searchQuery = pageSearch.searchQuery;
  const setSearchQuery = pageSearch.setSearchQuery;
  const idleEndElement =
    emptyEndElement ??
    (showIdleSearchFiltersButton ? (
      <OpenSearchFiltersButton
        disabled={!isAllowedPage || disabled}
        showLabel={false}
      />
    ) : undefined);

  return (
    <InputGroup
      w="full"
      flex="1"
      minW={0}
      border={border}
      borderColor={borderColor}
      borderRadius={borderRadius}
      _focusWithin={{
        borderColor: borderColorOnFocus,
      }}
      endElement={
        !hideInternalClearButton && searchQuery ? (
          <CloseButton
            size="xs"
            variant="plain"
            onClick={() => {
              setSearchQuery("");
              onCloseSuccess?.();
            }}
          />
        ) : (
          idleEndElement
        )
      }
    >
      {singleLine ? (
        <Input
          w="full"
          minW={0}
          ref={inputRef}
          placeholder={placeholder}
          size="sm"
          fontSize={{ base: "14px", md: "sm" }} // Observe it this solves expanding input and breaking design on mobile
          value={searchQuery || ""}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={onFocusAction}
          onBlur={onBlurAction}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setSearchQuery("");
            }
          }}
          style={{
            border: "none",
            borderRadius: 0,
            overscrollBehavior: "contain",
            touchAction: "manipulation",
            resize: "none",
            overflow: "hidden",
            width: "100%",
          }}
          disabled={disabled}
        />
      ) : (
        <Textarea
          w="full"
          minW={0}
          ref={textareaRef}
          placeholder={placeholder}
          size="sm"
          fontSize={{ base: "16px", md: "sm" }}
          value={searchQuery || ""}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={onFocusAction}
          onBlur={onBlurAction}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setSearchQuery("");
            }
          }}
          style={{
            border: "none",
            borderRadius: 0,
            overscrollBehavior: "contain",
            touchAction: "manipulation",
            width: "100%",
            maxHeight: "320px",
            overflowY: "auto",
          }}
          autoresize={true}
          rows={1}
          autoFocus={false}
          disabled={disabled}
        />
      )}
    </InputGroup>
  );
}

export default React.memo(NavbarSearchInput);
